import type { SessionReplaySignal } from "@backchannel/tax-contracts";

const REDACTED = "[redacted]";

const JWT_RE = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const BCTAX_KEY_RE = /bctax_[A-Za-z0-9_-]+/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const LONG_DIGITS_RE = /\d{11,}/g;
/** Absolute or path-ish URLs that may carry a query or hash. */
const URL_IN_TEXT_RE =
  /(?:https?:\/\/[^\s"'<>]+|\/[^\s"'<>?#]*(?:\?[^\s"'<>#]*)?(?:#[^\s"'<>]*)?)/g;

/** rrweb Meta event (document href / viewport). */
const RRWEB_META = 4;

function stripUrlQueryHash(url: string): string {
  try {
    if (/^https?:\/\//i.test(url)) {
      const parsed = new URL(url);
      return `${parsed.origin}${parsed.pathname}`;
    }
    const parsed = new URL(url, "https://tax.invalid");
    return parsed.pathname;
  } catch {
    const cut = url.search(/[?#]/);
    return cut === -1 ? url : url.slice(0, cut);
  }
}

/** Replace secrets, e-mails and long digit runs; strip URL query/hash. */
export function scrubText(input: string): string {
  let text = input;
  text = text.replace(URL_IN_TEXT_RE, (match) => stripUrlQueryHash(match));
  text = text.replace(JWT_RE, REDACTED);
  text = text.replace(BCTAX_KEY_RE, REDACTED);
  text = text.replace(BEARER_RE, `Bearer ${REDACTED}`);
  text = text.replace(EMAIL_RE, REDACTED);
  text = text.replace(LONG_DIGITS_RE, REDACTED);
  return text;
}

function scrubMetaHref(events: Array<Record<string, unknown>>): void {
  for (const event of events) {
    if (event.type !== RRWEB_META) continue;
    const data = event.data;
    if (!data || typeof data !== "object" || Array.isArray(data)) continue;
    const record = data as Record<string, unknown>;
    const href = record.href;
    if (typeof href !== "string") continue;
    try {
      const parsed = new URL(href);
      record.href = `${parsed.origin}${parsed.pathname}`;
    } catch {
      record.href = stripUrlQueryHash(href);
    }
  }
}

/**
 * Scrub rrweb events: token patterns on the JSON string, then Meta `href`
 * reduced to origin + pathname (never query/hash).
 */
export function scrubEvents(
  events: ReadonlyArray<Record<string, unknown>>,
): Array<Record<string, unknown>> {
  let serialized = JSON.stringify(events);
  serialized = serialized.replace(JWT_RE, REDACTED);
  serialized = serialized.replace(BCTAX_KEY_RE, REDACTED);
  serialized = serialized.replace(BEARER_RE, `Bearer ${REDACTED}`);
  try {
    const parsed = JSON.parse(serialized) as Array<Record<string, unknown>>;
    scrubMetaHref(parsed);
    return parsed;
  } catch {
    return [];
  }
}

function truncate(value: string | undefined, max: number): string | undefined {
  if (value === undefined) return undefined;
  return value.length <= max ? value : value.slice(0, max);
}

function scrubRoute(route: string | undefined): string | undefined {
  if (route === undefined) return undefined;
  const path = scrubText(stripUrlQueryHash(route));
  return truncate(path.startsWith("/") ? path : `/${path}`, 512);
}

/** Scrub and truncate a client signal to the contract maxima. */
export function scrubSignal(signal: SessionReplaySignal): SessionReplaySignal {
  return {
    ...signal,
    message: truncate(signal.message !== undefined ? scrubText(signal.message) : undefined, 500),
    stack: truncate(signal.stack !== undefined ? scrubText(signal.stack) : undefined, 2_000),
    source: truncate(
      signal.source !== undefined ? scrubText(stripUrlQueryHash(signal.source)) : undefined,
      300,
    ),
    route: scrubRoute(signal.route),
    path: scrubRoute(signal.path),
  };
}
